import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Form,
  Input,
  Button,
  DatePicker,
  Switch,
  Tabs,
  Table,
  Space,
  Card,
  Typography,
  message,
  Modal,
  Spin,
  Popconfirm,
  Tooltip,
  Divider,
  Descriptions,
  Tag,
  Avatar,
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  DeleteOutlined,
  QrcodeOutlined,
  TeamOutlined,
  EnvironmentOutlined,
  PictureOutlined,
  EyeOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlayCircleOutlined,
  UserOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import dayjs from 'dayjs';
import PagesWrapper from '../../../PagesWrapper';
import ImageDropZone from '../../../components/ImageDropZone';
import RichTextEditor from '../../../components/RichTextEditor';
import { AccessBanner, OwnershipManager } from '../../../components/Ownership';
import { useOwnership } from '../../../hooks';
import * as eventsService from '../../../services/eventsService';
import { getFileUrl } from '../../../utils/urlUtils';
import { useAuth } from '../../../context/AuthContext';
import { AppRoles } from '../../../services/authService';
import styles from './EventEditor.module.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

const EventEditor = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isEditing = !!id;
  const { hasRole } = useAuth();
  const isAdmin = hasRole(AppRoles.Admin);

  // State
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [bannerImage, setBannerImage] = useState(null);
  const [clearBannerImage, setClearBannerImage] = useState(false);
  const [richContent, setRichContent] = useState('');
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [bannerPreviewVisible, setBannerPreviewVisible] = useState(false);
  const [ownershipModalVisible, setOwnershipModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  // Memoize banner preview URL to prevent memory leaks
  const bannerPreviewUrl = useMemo(() => {
    if (bannerImage instanceof File) {
      return URL.createObjectURL(bannerImage);
    }
    return null;
  }, [bannerImage]);

  // Cleanup object URL when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (bannerPreviewUrl) {
        URL.revokeObjectURL(bannerPreviewUrl);
      }
    };
  }, [bannerPreviewUrl]);
  
  // Registrations state
  const [registrations, setRegistrations] = useState([]);
  const [registrationsLoading, setRegistrationsLoading] = useState(false);
  const [registrationsPagination, setRegistrationsPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [registrationModalVisible, setRegistrationModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Ownership hook
  const {
    ownershipInfo,
    canManageOwnership,
    shareWith,
    revoke,
    getAvailableUsers,
    availableUsers,
    loading: ownershipLoading,
    loadingAvailableUsers,
  } = useOwnership('Event', id);

  // Fetch event data
  useEffect(() => {
    if (isEditing) {
      fetchEvent();
    }
  }, [id]);

  const fetchEvent = async () => {
    setLoading(true);
    try {
      const data = await eventsService.getEvent(id);
      setEvent(data);
      setRichContent(data.richContent || '');
      form.setFieldsValue({
        title: data.title,
        startDateTime: dayjs(data.startDateTime),
        endDateTime: dayjs(data.endDateTime),
        location: data.location,
        meetingUrl: data.meetingUrl || '',
        purpose: data.purpose,
        organization: data.organization,
        organizer: data.organizer,
        isPublished: data.isPublished,
        isFeatured: data.isFeatured,
      });
    } catch (error) {
      message.error(t('events.fetchError', 'Failed to load event'));
      navigate('/admin/events');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrations = useCallback(async (page = 1) => {
    if (!id) return;
    setRegistrationsLoading(true);
    try {
      const response = await eventsService.getEventRegistrations(id, {
        page,
        pageSize: registrationsPagination.pageSize,
      });
      setRegistrations(response.items || []);
      setRegistrationsPagination(prev => ({
        ...prev,
        current: response.page,
        total: response.totalCount,
      }));
    } catch (error) {
      message.error(t('events.registrations.fetchError', 'Failed to load registrations'));
    } finally {
      setRegistrationsLoading(false);
    }
  }, [id, registrationsPagination.pageSize, t]);

  useEffect(() => {
    if (activeTab === 'registrations' && id) {
      fetchRegistrations();
    }
  }, [activeTab, id]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const eventData = {
        title: values.title,
        startDateTime: values.startDateTime.toISOString(),
        endDateTime: values.endDateTime.toISOString(),
        location: values.location,
        meetingUrl: values.meetingUrl?.trim() ?? '',
        purpose: values.purpose || null,
        organization: values.organization || null,
        organizer: values.organizer || null,
        richContent: richContent || null,
        isPublished: event ? event.isPublished || false : false,
        isFeatured: values.isFeatured || false,
        clearBannerImage: clearBannerImage,
      };

      if (isEditing) {
        await eventsService.updateEvent(id, eventData, bannerImage);
        message.success(t('events.updateSuccess', 'Event updated successfully'));
        fetchEvent();
      } else {
        const newEvent = await eventsService.createEvent(eventData, bannerImage);
        message.success(t('events.createSuccess', 'Event created successfully'));
        navigate(`/admin/events/${newEvent.id}`);
      }
      
      setBannerImage(null);
      setClearBannerImage(false);
    } catch (error) {
      console.log('Save error:', error);
      if (error.errorFields) return; // Form validation error
      message.error(t('events.saveError', 'Failed to save event'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await eventsService.deleteEvent(id);
      message.success(t('events.deleteSuccess', 'Event deleted successfully'));
      navigate('/admin/events');
    } catch (error) {
      message.error(t('events.deleteError', 'Failed to delete event'));
    }
  };

  const handleTogglePublish = async () => {
    if (!event) return;
    
    setPublishing(true);
    try {
      const newPublishState = !event.isPublished;
      await eventsService.updateEvent(id, { isPublished: newPublishState }, null);
      
      setEvent(prev => ({ ...prev, isPublished: newPublishState }));
      form.setFieldValue('isPublished', newPublishState);
      
      message.success(
        newPublishState 
          ? t('events.publishSuccess', 'Event published successfully')
          : t('events.unpublishSuccess', 'Event unpublished successfully')
      );
    } catch (error) {
      message.error(t('events.publishError', 'Failed to update publish status'));
    } finally {
      setPublishing(false);
    }
  };

  const handleDeleteRegistration = async (registrationId) => {
    try {
      await eventsService.deleteEventRegistration(id, registrationId);
      message.success(t('events.registrations.deleteSuccess', 'Registration deleted'));
      fetchRegistrations(registrationsPagination.current);
    } catch (error) {
      message.error(t('events.registrations.deleteError', 'Failed to delete registration'));
    }
  };

  const handleBulkDeleteRegistrations = async () => {
    if (selectedRowKeys.length === 0) return;
    
    setBulkDeleting(true);
    try {
      // Delete all selected registrations in parallel
      await Promise.all(
        selectedRowKeys.map(regId => eventsService.deleteEventRegistration(id, regId))
      );
      message.success(
        t('events.registrations.bulkDeleteSuccess', '{{count}} registration(s) deleted', { count: selectedRowKeys.length })
      );
      setSelectedRowKeys([]);
      fetchRegistrations(registrationsPagination.current);
    } catch (error) {
      message.error(t('events.registrations.bulkDeleteError', 'Failed to delete some registrations'));
      // Refresh anyway to show current state
      fetchRegistrations(registrationsPagination.current);
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleClearBannerImage = () => {
    Modal.confirm({
      title: t('events.form.removeBannerConfirm', 'Remove banner image?'),
      content: t('events.form.removeBannerHint', 'The image will be deleted when you save the event.'),
      okText: t('common.yes', 'Yes'),
      cancelText: t('common.no', 'No'),
      onOk: () => {
        setClearBannerImage(true);
      },
    });
  };

  const handleDownloadQrCode = () => {
    const svg = document.getElementById('event-editor-qr-code');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `${event?.title || 'event'}-qrcode.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handleViewRegistration = (registration) => {
    setSelectedRegistration(registration);
    setRegistrationModalVisible(true);
  };

  const handleExportRegistrations = async () => {
    try {
      // Fetch all registrations for export (not just current page)
      const response = await eventsService.getEventRegistrations(id, {
        page: 1,
        pageSize: 10000, // Get all registrations
      });
      
      const allRegistrations = response.items || [];
      
      if (allRegistrations.length === 0) {
        message.warning(t('events.registrations.noDataToExport', 'No registrations to export'));
        return;
      }

      // CSV headers
      const headers = [
        t('events.registrations.firstName', 'First Name'),
        t('events.registrations.lastName', 'Last Name'),
        t('events.registrations.email', 'Email'),
        t('events.registrations.phone', 'Phone'),
        t('events.registrations.remarks', 'Remarks'),
        t('events.registrations.registeredAt', 'Registered At'),
        t('events.registrations.reminderStatus', 'Reminder Status'),
      ];

      // CSV rows
      const rows = allRegistrations.map(reg => [
        reg.firstName,
        reg.lastName,
        reg.email,
        reg.phone || '',
        reg.remarks || '',
        dayjs(reg.registeredAt).format('DD/MM/YYYY HH:mm'),
        reg.reminderSentAt
          ? t('events.registrations.reminderSentAtValue', 'Sent {{date}}', {
              date: dayjs(reg.reminderSentAt).format('DD/MM/YYYY HH:mm'),
            })
          : t('events.registrations.reminderPending', 'Pending'),
      ]);

      // Build CSV content with proper escaping
      const escapeCSV = (value) => {
        const str = String(value);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const csvContent = [
        headers.map(escapeCSV).join(','),
        ...rows.map(row => row.map(escapeCSV).join(',')),
      ].join('\n');

      // Add BOM for Excel UTF-8 compatibility
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${event?.title || 'event'}-registrations-${dayjs().format('YYYY-MM-DD')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.success(t('events.registrations.exportSuccess', 'Registrations exported successfully'));
    } catch (error) {
      message.error(t('events.registrations.exportError', 'Failed to export registrations'));
    }
  };

  const registrationColumns = [
    {
      title: t('events.registrations.name', 'Name'),
      key: 'name',
      render: (_, record) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: t('events.registrations.email', 'Email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('events.registrations.phone', 'Phone'),
      dataIndex: 'phone',
      key: 'phone',
      render: (text) => text || '-',
    },
    {
      title: t('events.registrations.remarks', 'Remarks'),
      dataIndex: 'remarks',
      key: 'remarks',
      width: 200,
      render: (text) => (
        <span className={styles.remarksCell}>
          {text || '-'}
        </span>
      ),
    },
    {
      title: t('events.registrations.registeredAt', 'Registered'),
      dataIndex: 'registeredAt',
      key: 'registeredAt',
      render: (text, record) => (
        <div>
          <div>{dayjs(text).format('DD/MM/YYYY HH:mm')}</div>
          {record.modifiedAt && (
            <div style={{ fontSize: '12px', color: '#888' }}>
              {t('events.registrations.modifiedAt', 'Modified')}: {dayjs(record.modifiedAt).format('DD/MM/YYYY HH:mm')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('events.registrations.reminder', 'Reminder'),
      dataIndex: 'reminderSentAt',
      key: 'reminderSentAt',
      width: 180,
      render: (text) => (
        text ? (
          <div>
            <Tag color="green" icon={<CheckCircleOutlined />}>
              {t('events.registrations.reminderSent', 'Sent')}
            </Tag>
            <div style={{ fontSize: '12px', color: '#888' }}>
              {dayjs(text).format('DD/MM/YYYY HH:mm')}
            </div>
          </div>
        ) : (
          <Tag color="default" icon={<CloseCircleOutlined />}>
            {t('events.registrations.reminderPending', 'Pending')}
          </Tag>
        )
      ),
    },
    {
      title: t('common.actions', 'Actions'),
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Popconfirm
          title={t('events.registrations.deleteConfirm', 'Delete this registration?')}
          onConfirm={() => handleDeleteRegistration(record.id)}
          okText={t('common.yes', 'Yes')}
          cancelText={t('common.no', 'No')}
        >
          <Tooltip title={t('common.delete', 'Delete')}>
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'details',
      label: t('events.tabs.details', 'Event Details'),
      children: (
        <div className={styles.formContainer}>
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              isPublished: false,
              isFeatured: false,
            }}
          >
            <div className={styles.formGrid}>
              <div className={styles.mainColumn}>
                <Form.Item
                  name="title"
                  label={t('events.form.title', 'Title')}
                  rules={[{ required: true, message: t('events.form.titleRequired', 'Title is required') }]}
                >
                  <Input placeholder={t('events.form.titlePlaceholder', 'Enter event title')} />
                </Form.Item>

                <div className={styles.dateRow}>
                  <Form.Item
                    name="startDateTime"
                    label={t('events.form.startDateTime', 'Start Date & Time')}
                    rules={[{ required: true, message: t('events.form.startRequired', 'Start date/time is required') }]}
                  >
                    <DatePicker
                      showTime
                      format="DD/MM/YYYY HH:mm"
                      placeholder={t('events.form.startPlaceholder', 'Select start')}
                      className={styles.datePicker}
                    />
                  </Form.Item>
                  <Form.Item
                    name="endDateTime"
                    label={t('events.form.endDateTime', 'End Date & Time')}
                    rules={[{ required: true, message: t('events.form.endRequired', 'End date/time is required') }]}
                  >
                    <DatePicker
                      showTime
                      format="DD/MM/YYYY HH:mm"
                      placeholder={t('events.form.endPlaceholder', 'Select end')}
                      className={styles.datePicker}
                    />
                  </Form.Item>
                </div>

                <Form.Item
                  name="location"
                  label={t('events.form.location', 'Location')}
                  rules={[{ required: true, message: t('events.form.locationRequired', 'Location is required') }]}
                >
                  <Input 
                    prefix={<EnvironmentOutlined />}
                    placeholder={t('events.form.locationPlaceholder', 'Enter event location')} 
                  />
                </Form.Item>

                <Form.Item
                  name="meetingUrl"
                  label={t('events.form.meetingUrl', 'Remote Attendance Link')}
                  rules={[
                    {
                      type: 'url',
                      warningOnly: true,
                      message: t('events.form.meetingUrlInvalid', 'Enter a valid absolute URL, for example https://...'),
                    },
                  ]}
                >
                  <Input
                    prefix={<LinkOutlined />}
                    placeholder={t('events.form.meetingUrlPlaceholder', 'https://teams.microsoft.com/l/meetup-join/...')}
                  />
                </Form.Item>

                <Form.Item
                  name="purpose"
                  label={t('events.form.purpose', 'Purpose')}
                >
                  <TextArea 
                    rows={3}
                    placeholder={t('events.form.purposePlaceholder', 'Describe the purpose of the event')} 
                    maxLength={1000}
                    showCount
                  />
                </Form.Item>

                <div className={styles.twoColumns}>
                  <Form.Item
                    name="organization"
                    label={t('events.form.organization', 'Organization')}
                  >
                    <Input placeholder={t('events.form.organizationPlaceholder', 'Hosting organization')} />
                  </Form.Item>
                  <Form.Item
                    name="organizer"
                    label={t('events.form.organizer', 'Organizer')}
                  >
                    <Input placeholder={t('events.form.organizerPlaceholder', 'Event organizer name')} />
                  </Form.Item>
                </div>

                <Divider>{t('events.form.content', 'Event Content')}</Divider>

                <div className={styles.richEditorContainer}>
                  <RichTextEditor
                    value={richContent}
                    onChange={setRichContent}
                    maxLength={5000}
                    placeholder={t('events.form.contentPlaceholder', 'Add detailed event description, agenda, requirements...')}
                    parentType="Event"
                    parentId={id ? parseInt(id, 10) : null}
                  />
                </div>
              </div>

              <div className={styles.sideColumn}>
                <Card title={t('events.form.bannerImage', 'Banner Image')} size="small">
                  <ImageDropZone
                    value={bannerImage}
                    onChange={setBannerImage}
                    existingUrl={!clearBannerImage && event?.bannerImageUrl ? getFileUrl(event.bannerImageUrl) : null}
                    onClearExisting={handleClearBannerImage}
                  />
                  <div className={styles.bannerHintRow}>
                    <div className={styles.bannerHint}>
                      <PictureOutlined className={styles.bannerHintIcon} />
                      <span>{t('events.form.bannerHint', 'Recommended: 16:9 ratio (1200×675px)')}</span>
                    </div>
                    {(bannerImage || (event?.bannerImageUrl && !clearBannerImage)) && (
                      <Tooltip title={t('events.form.preview', 'Preview')}>
                        <Button
                          type="text"
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={() => setBannerPreviewVisible(true)}
                          className={styles.previewButton}
                        />
                      </Tooltip>
                    )}

                   
                  </div>
                </Card>

                {isAdmin && (
                  <Card title={t('events.form.settings', 'Settings')} size="small" className={styles.settingsCard}>
                    <Form.Item
                      name="isFeatured"
                      label={t('events.form.isFeatured', 'Featured')}
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                    <Text type="secondary" className={styles.settingHint}>
                      {t('events.form.featuredHint', 'Featured events appear in the trending section')}
                    </Text>
                  </Card>
                )}
              </div>
            </div>
          </Form>
        </div>
      ),
    },
    ...(isEditing ? [{
      key: 'registrations',
      label: (
        <span>
          <TeamOutlined /> {t('events.tabs.registrations', 'Registrations')} ({event?.registrationCount || 0})
        </span>
      ),
      children: (
        <div className={styles.registrationsContainer}>
          <div className={styles.registrationsHeader}>
            <div className={styles.registrationsHeaderLeft}>
              {selectedRowKeys.length > 0 ? (
                <Space>
                  <Text strong>
                    {t('events.registrations.selectedCount', '{{count}} selected', { count: selectedRowKeys.length })}
                  </Text>
                  <Popconfirm
                    title={t('events.registrations.bulkDeleteConfirm', 'Delete {{count}} selected registration(s)?', { count: selectedRowKeys.length })}
                    onConfirm={handleBulkDeleteRegistrations}
                    okText={t('common.yes', 'Yes')}
                    cancelText={t('common.no', 'No')}
                  >
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      loading={bulkDeleting}
                    >
                      {t('events.registrations.deleteSelected', 'Delete Selected')}
                    </Button>
                  </Popconfirm>
                  <Button onClick={() => setSelectedRowKeys([])}>
                    {t('common.clearSelection', 'Clear')}
                  </Button>
                </Space>
              ) : (
                <Space direction="vertical" size={2}>
                  <Text type="secondary">
                    {t('events.registrations.clickToView', 'Click on a row to view full details')}
                  </Text>
                  <Text type="secondary">
                    {t('events.registrations.reminderInfo', 'Automatic reminder emails are sent about 1 hour before start for published events.')}
                  </Text>
                </Space>
              )}
            </div>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportRegistrations}
              disabled={registrations.length === 0}
            >
              {t('events.registrations.exportCSV', 'Export CSV')}
            </Button>
          </div>
          <Table
            dataSource={registrations}
            columns={registrationColumns}
            rowKey="id"
            loading={registrationsLoading}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
              preserveSelectedRowKeys: true,
            }}
            pagination={{
              current: registrationsPagination.current,
              pageSize: registrationsPagination.pageSize,
              total: registrationsPagination.total,
              showSizeChanger: false,
            }}
            onChange={(pag) => fetchRegistrations(pag.current)}
            scroll={{ x: 800 }}
            onRow={(record) => ({
              onClick: () => handleViewRegistration(record),
              style: { cursor: 'pointer' },
            })}
          />
        </div>
      ),
    }] : []),
  ];

  if (loading) {
    return (
      <PagesWrapper>
        <div className={styles.loadingContainer}>
          <Spin size="large" />
        </div>
      </PagesWrapper>
    );
  }

  return (
    <PagesWrapper>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/events')}>
              {t('common.back', 'Back')}
            </Button>
            <h1 className={styles.pageTitle}>{event?.title || t('events.newEvent', 'New Event')}</h1>
            <Tag color={event?.isPublished ? 'green' : 'default'}>
              {event?.isPublished ? t('events.published', 'Published') : t('events.draft', 'Draft')}
            </Tag>
            {event?.addedByName && (
              <Tooltip title={t('events.createdBy', 'Created by')}>
                <Space size="small" className={styles.createdBy}>
                  <Avatar 
                    size="small" 
                    src={getFileUrl(event.addedByProfileImageUrl)}
                    icon={!event.addedByProfileImageUrl && <UserOutlined />}
                  />
                  <Text type="secondary">{event.addedByName}</Text>
                </Space>
              </Tooltip>
            )}
          </div>
          <Space className={styles.headerActions}>
            {isEditing && event?.isPublished && (
              <>
                <Tooltip title={t('events.openEvent', 'Open Event')}>
                  <Button
                    icon={<PlayCircleOutlined />}
                    onClick={() => window.open(`/events/${id}`, '_blank')}
                  >
                    {t('events.openEvent', 'Open Event')}
                  </Button>
                </Tooltip>
                <Tooltip title={t('events.qrCode', 'QR Code')}>
                  <Button
                    icon={<QrcodeOutlined />}
                    onClick={() => setQrModalVisible(true)}
                  >
                    {t('events.qrCode', 'QR Code')}
                  </Button>
                </Tooltip>
              </>
            )}
            {isEditing && canManageOwnership && (
              <Tooltip title={t('ownership.manageAccess', 'Manage Access')}>
                <Button
                  icon={<TeamOutlined />}
                  onClick={() => setOwnershipModalVisible(true)}
                >
                  {t('ownership.share', 'Share')}
                </Button>
              </Tooltip>
            )}
            {isEditing && (
              <Button
                icon={event?.isPublished ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                onClick={handleTogglePublish}
                loading={publishing}
              >
                {event?.isPublished ? t('events.unpublish', 'Unpublish') : t('events.publish', 'Publish')}
              </Button>
            )}
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
              {t('common.save', 'Save')}
            </Button>
            {isEditing && (
              <Popconfirm
                title={t('events.deleteConfirm', 'Are you sure you want to delete this event?')}
                onConfirm={handleDelete}
                okText={t('common.yes', 'Yes')}
                cancelText={t('common.no', 'No')}
              >
                <Button danger icon={<DeleteOutlined />}>
                  {t('common.delete', 'Delete')}
                </Button>
              </Popconfirm>
            )}
          </Space>
        </div>

        {/* Access Banner for shared/admin users */}
        {isEditing && ownershipInfo && (
          <AccessBanner
            accessLevel={ownershipInfo.accessLevel}
            ownerInfo={ownershipInfo.mainOwner}
            canEdit={ownershipInfo.canEdit}
            canDelete={ownershipInfo.canDelete}
          />
        )}

        {/* Tabs */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />

        {/* Banner Preview Modal */}
        <Modal
          title={t('events.form.bannerPreviewTitle', 'Banner Preview (16:9)')}
          open={bannerPreviewVisible}
          onCancel={() => setBannerPreviewVisible(false)}
          footer={null}
          centered
          width={700}
        >
          <div className={styles.bannerPreviewModal}>
            <div className={styles.bannerPreviewFrame}>
              <img 
                src={bannerPreviewUrl || getFileUrl(event?.bannerImageUrl)}
                alt={t('events.form.bannerPreviewAlt', 'Banner preview')}
                className={styles.bannerPreviewImage}
              />
            </div>
            <Text type="secondary" className={styles.bannerPreviewNote}>
              {t('events.form.bannerPreviewNote', 'This is how your banner will appear on the event page. Areas outside the 16:9 frame will be cropped.')}
            </Text>
          </div>
        </Modal>

        {/* QR Code Modal */}
        <Modal
          title={t('events.qrCodeTitle', 'Event QR Code')}
          open={qrModalVisible}
          onCancel={() => setQrModalVisible(false)}
          footer={[
            <Button key="download" type="primary" onClick={handleDownloadQrCode}>
              {t('events.downloadQr', 'Download QR Code')}
            </Button>,
            <Button key="close" onClick={() => setQrModalVisible(false)}>
              {t('common.close', 'Close')}
            </Button>,
          ]}
          centered
        >
          {event && (
            <div className={styles.qrContainer}>
              <QRCodeSVG
                id="event-editor-qr-code"
                value={`${window.location.origin}/events/${event.id}`}
                size={256}
                level="H"
                includeMargin
              />
              <Text className={styles.qrEventName}>{event.title}</Text>
              <Text type="secondary" className={styles.qrUrl}>
                {window.location.origin}/events/{event.id}
              </Text>
            </div>
          )}
        </Modal>

        {/* Registration Detail Modal */}
        <Modal
          title={t('events.registrations.detailTitle', 'Registration Details')}
          open={registrationModalVisible}
          onCancel={() => {
            setRegistrationModalVisible(false);
            setSelectedRegistration(null);
          }}
          footer={[
            <Popconfirm
              key="delete"
              title={t('events.registrations.deleteConfirm', 'Delete this registration?')}
              onConfirm={() => {
                handleDeleteRegistration(selectedRegistration?.id);
                setRegistrationModalVisible(false);
                setSelectedRegistration(null);
              }}
              okText={t('common.yes', 'Yes')}
              cancelText={t('common.no', 'No')}
            >
              <Button danger icon={<DeleteOutlined />}>
                {t('common.delete', 'Delete')}
              </Button>
            </Popconfirm>,
            <Button key="close" onClick={() => {
              setRegistrationModalVisible(false);
              setSelectedRegistration(null);
            }}>
              {t('common.close', 'Close')}
            </Button>,
          ]}
          centered
          width={600}
        >
          {selectedRegistration && (
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label={t('events.registrations.name', 'Name')}>
                {selectedRegistration.firstName} {selectedRegistration.lastName}
              </Descriptions.Item>
              <Descriptions.Item label={t('events.registrations.email', 'Email')}>
                <a href={`mailto:${selectedRegistration.email}`}>{selectedRegistration.email}</a>
              </Descriptions.Item>
              <Descriptions.Item label={t('events.registrations.phone', 'Phone')}>
                {selectedRegistration.phone ? (
                  <a href={`tel:${selectedRegistration.phone}`}>{selectedRegistration.phone}</a>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('events.registrations.registeredAt', 'Registered')}>
                {dayjs(selectedRegistration.registeredAt).format('DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label={t('events.registrations.reminder', 'Reminder')}>
                {selectedRegistration.reminderSentAt ? (
                  <Space direction="vertical" size={2}>
                    <Tag color="green" icon={<CheckCircleOutlined />}>
                      {t('events.registrations.reminderSent', 'Sent')}
                    </Tag>
                    <Text>
                      {dayjs(selectedRegistration.reminderSentAt).format('DD/MM/YYYY HH:mm')}
                    </Text>
                  </Space>
                ) : (
                  <Tag color="default" icon={<CloseCircleOutlined />}>
                    {t('events.registrations.reminderPending', 'Pending')}
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('events.registrations.remarks', 'Remarks')}>
                <div className={styles.registrationRemarks}>
                  {selectedRegistration.remarks || '-'}
                </div>
              </Descriptions.Item>
            </Descriptions>
          )}
        </Modal>

        {/* Ownership Manager Modal */}
        <OwnershipManager
          open={ownershipModalVisible}
          onClose={() => setOwnershipModalVisible(false)}
          ownershipInfo={ownershipInfo}
          onShare={shareWith}
          onRevoke={revoke}
          onGetAvailableUsers={getAvailableUsers}
          availableUsers={availableUsers}
          loading={ownershipLoading}
          loadingAvailableUsers={loadingAvailableUsers}
          canManageOwnership={canManageOwnership}
        />
      </div>
    </PagesWrapper>
  );
};

export default EventEditor;
